import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useAllTags,
  useTypeTagIds,
  useTagMutations,
} from "@/hooks/useSparePartTypes";

export default function TagMultiSelect({ partTypeId }: { partTypeId: string }) {
  const { data: allTags = [] } = useAllTags();
  const { data: linkedIds = [] } = useTypeTagIds(partTypeId);
  const { createTag, link, unlink } = useTagMutations(partTypeId);
  const [newName, setNewName] = useState("");
  const [open, setOpen] = useState(false);

  const linked = allTags.filter((t) => linkedIds.includes(t.id));
  const unlinked = allTags.filter((t) => !linkedIds.includes(t.id));

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const tag = await createTag.mutateAsync(newName.trim());
    if (tag) await link.mutateAsync(tag.id);
    setNewName("");
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {linked.map((t) => (
        <Badge key={t.id} variant="secondary" className="gap-1 pr-1">
          {t.name}
          <button
            type="button"
            onClick={() => unlink.mutate(t.id)}
            className="hover:bg-muted rounded-sm p-0.5"
            aria-label={`Remove tag ${t.name}`}
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" className="h-6 gap-1 text-xs">
            <Plus className="w-3 h-3" /> Tag
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start">
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">Available tags</div>
            <div className="flex flex-wrap gap-1">
              {unlinked.length === 0 ? (
                <span className="text-xs text-muted-foreground">None — create one below.</span>
              ) : (
                unlinked.map((t) => (
                  <Badge
                    key={t.id}
                    variant="outline"
                    className="cursor-pointer hover:bg-muted"
                    onClick={() => link.mutate(t.id)}
                  >
                    + {t.name}
                  </Badge>
                ))
              )}
            </div>
            <div className="flex gap-1 pt-2 border-t">
              <Input
                placeholder="New tag…"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="h-7 text-xs"
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
              <Button size="sm" className="h-7" onClick={handleCreate} disabled={!newName.trim()}>
                Add
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
